/**
 * MessageFormatter Service
 * Standardizes error and success message formatting and types
 */

class MessageFormatter {
  /**
   * Format error message for user display
   * @param {string|Error} error - Error message or Error object
   * @param {Object} options - Formatting options
   * @returns {Object} { type: 'error', message: string }
   */
  static error(error, options = {}) {
    let message;

    if (error instanceof Error) {
      message = error.message;
    } else if (typeof error === 'string') {
      message = error;
    } else {
      message = 'An error occurred';
    }

    return {
      type: 'error',
      message,
      ...options
    };
  }

  /**
   * Format success message
   * @param {string} message - Success message
   * @param {Object} options - Formatting options
   * @returns {Object} { type: 'success', message: string }
   */
  static success(message = 'Operation completed successfully', options = {}) {
    return {
      type: 'success',
      message,
      ...options
    };
  }

  /**
   * Format warning message
   * @param {string} message - Warning message
   * @param {Object} options - Formatting options
   * @returns {Object} { type: 'warning', message: string }
   */
  static warning(message, options = {}) {
    return {
      type: 'warning',
      message: message || 'Warning: Please check your action',
      ...options
    };
  }

  /**
   * Format info message
   * @param {string} message - Info message
   * @param {Object} options - Formatting options
   * @returns {Object} { type: 'info', message: string }
   */
  static info(message, options = {}) {
    return {
      type: 'info',
      message: message || 'Information',
      ...options
    };
  }

  /**
   * Format generic message object
   * @param {string} type - Message type (error, success, warning, info)
   * @param {string} message - Message text
   * @param {Object} options - Additional options
   * @returns {Object} Formatted message
   */
  static message(type, message, options = {}) {
    if (!['error', 'success', 'warning', 'info'].includes(type)) {
      console.warn(`Unknown message type: ${type}`);
      return { type: 'info', message, ...options };
    }

    return { type, message, ...options };
  }

  /**
   * Format validation error messages
   * @param {Object|Array} errors - Validation errors
   * @param {Object} options - Formatting options
   * @returns {Object} { type: 'error', message: string }
   */
  static validationError(errors, options = {}) {
    let message;

    if (Array.isArray(errors)) {
      if (errors.length === 0) {
        message = 'Validation error';
      } else if (errors.length === 1) {
        message = (errors[0] && errors[0].message) || errors[0] || 'Validation error';
      } else {
        message = `${errors.length} validation errors found. Please check your input.`;
      }
    } else if (typeof errors === 'object') {
      const errorMessages = Object.values(errors)
        .flat()
        .filter(e => e)
        .slice(0, 2); // Show first 2 errors

      if (errorMessages.length > 0) {
        message = errorMessages.join('. ') + (Object.keys(errors).length > 2 ? '...' : '');
      } else {
        message = 'Validation error';
      }
    } else {
      message = errors || 'Validation error';
    }

    return {
      type: 'error',
      message,
      ...options
    };
  }

  /**
   * Format permission/authorization error
   * @param {string} action - Action that was denied
   * @param {string} resource - Resource being accessed
   * @returns {Object} Formatted error message
   */
  static permissionError(action = 'perform this action', resource = '') {
    let message = `You don't have permission to ${action}`;
    if (resource) {
      message += ` on ${resource}`;
    }

    return {
      type: 'error',
      message
    };
  }

  /**
   * Format network/connectivity error
   * @returns {Object} Formatted error message
   */
  static networkError() {
    return {
      type: 'error',
      message: 'Network error. Please check your connection and try again.'
    };
  }

  /**
   * Format timeout error
   * @param {string} operation - Operation that timed out
   * @returns {Object} Formatted error message
   */
  static timeoutError(operation = 'Operation') {
    return {
      type: 'error',
      message: `${operation} timed out. Please try again.`
    };
  }

  /**
   * Format server error
   * @param {string} operation - Operation that failed
   * @returns {Object} Formatted error message
   */
  static serverError(operation = 'Operation') {
    return {
      type: 'error',
      message: `${operation} failed due to server error. Please try again later.`
    };
  }

  /**
   * Format payment-specific messages
   */
  static payment = {
    success: () => ({
      type: 'success',
      message: 'Payment processed successfully'
    }),
    error: (details = '') => ({
      type: 'error',
      message: `Payment failed${details ? ': ' + details : ''}`
    }),
    pending: () => ({
      type: 'info',
      message: 'Payment is being processed...'
    }),
    invalid: () => ({
      type: 'error',
      message: 'Invalid payment details. Please check and try again.'
    })
  };

  /**
   * Format registry-specific messages
   */
  static registry = {
    uploadSuccess: (count = 0) => ({
      type: 'success',
      message: count > 0 ? `Registry uploaded successfully (${count} transactions)` : 'Registry uploaded successfully'
    }),
    uploadError: (details = '') => ({
      type: 'error',
      message: `Registry upload failed${details ? ': ' + details : ''}`
    }),
    processing: () => ({
      type: 'info',
      message: 'Registry is being processed...'
    }),
    reprocessing: () => ({
      type: 'info',
      message: 'Registry is being reprocessed...'
    }),
    validationError: (errors = []) => ({
      type: 'error',
      message: `Registry validation failed${errors.length > 0 ? ': ' + errors.slice(0, 2).join(', ') + (errors.length > 2 ? '...' : '') : ''}`
    })
  };

  /**
   * Format transaction-specific messages
   */
  static transaction = {
    success: (action = 'Operation') => ({
      type: 'success',
      message: `${action} completed successfully`
    }),
    error: (action = 'Operation', details = '') => ({
      type: 'error',
      message: `${action} failed${details ? ': ' + details : ''}`
    }),
    declined: () => ({
      type: 'warning',
      message: 'Transaction was declined'
    }),
    timeout: () => ({
      type: 'warning',
      message: 'Transaction is pending. Please check status later.'
    })
  };

  /**
   * Merge multiple messages into one
   * @param {Array<Object>} messages - Messages to merge
   * @returns {Object} Merged message
   */
  static merge(messages = []) {
    if (messages.length === 0) return { type: 'info', message: '' };
    if (messages.length === 1) return messages[0];

    // Prioritize error > warning > info > success
    const priority = { error: 0, warning: 1, info: 2, success: 3 };
    const sorted = [...messages].sort((a, b) => 
      priority[a.type] - priority[b.type]
    );

    const merged = sorted[0];
    const additionalCount = messages.length - 1;

    if (additionalCount > 0) {
      merged.message += ` (and ${additionalCount} more message${additionalCount > 1 ? 's' : ''})`;
    }

    return merged;
  }
}

export default MessageFormatter;



// WEBPACK FOOTER //
// ./src/Services/MessageFormatter.js